/**
 * Food-category catalog — single source of truth shared by the owner's
 * dropdown and the map marker renderer.
 *
 * Each entry:
 *   slug:      stable DB value (snake_case, never change once shipped)
 *   label:     human-friendly string shown in the dropdown + helper UIs
 *   emoji:     fallback rendered inside the pin (unicode, universally available)
 *
 * We intentionally use emoji instead of bundling ~20 extra SVG paths — it
 * keeps the marker lightweight, universally legible at small sizes on
 * mobile, and paints with the device's native emoji font. The existing
 * orange teardrop pin stays intact underneath.
 */
export const FOOD_CATEGORIES = [
  { slug: "default",       label: "Default Pin",     emoji: "" },
  { slug: "burger",        label: "Burgers",         emoji: "🍔" },
  { slug: "coffee",        label: "Coffee",          emoji: "☕" },
  { slug: "tacos",         label: "Tacos",           emoji: "🌮" },
  { slug: "pizza",         label: "Pizza",           emoji: "🍕" },
  { slug: "bbq",           label: "BBQ",             emoji: "🍖" },
  { slug: "ice_cream",     label: "Ice Cream",       emoji: "🍦" },
  { slug: "desserts",      label: "Desserts",        emoji: "🧁" },
  { slug: "breakfast",     label: "Breakfast",       emoji: "🥞" },
  { slug: "sandwiches",    label: "Sandwiches",      emoji: "🥪" },
  { slug: "hot_dogs",      label: "Hot Dogs",        emoji: "🌭" },
  { slug: "seafood",       label: "Seafood",         emoji: "🦞" },
  { slug: "asian",         label: "Asian",           emoji: "🍜" },
  { slug: "mexican",       label: "Mexican",         emoji: "🌯" },
  { slug: "drinks",        label: "Drinks",          emoji: "🥤" },
  { slug: "lemonade",      label: "Lemonade",        emoji: "🍋" },
  { slug: "donuts",        label: "Donuts",          emoji: "🍩" },
  { slug: "cheesesteaks",  label: "Cheesesteaks",    emoji: "🧀" },
  { slug: "fish_sandwich", label: "Fish Sandwiches", emoji: "🐟" },
  { slug: "pretzels",      label: "Pretzels",        emoji: "🥨" },
  { slug: "popcorn",       label: "Popcorn",         emoji: "🍿" },
  { slug: "candy",         label: "Candy",           emoji: "🍬" },
  { slug: "other",         label: "Other",           emoji: "🍽️" },
];

const BY_SLUG = Object.fromEntries(FOOD_CATEGORIES.map((c) => [c.slug, c]));

/** Returns the catalog entry for a slug, or `null` if the slug isn't a
 *  known cuisine. Callers that want a guaranteed-non-null pin-style
 *  fallback (e.g. the map marker renderer) should use `getCategoryOrDefault`.
 *
 *  Important: this function used to silently fall back to the "default"
 *  catalog entry for ANY unknown string. That caused filter chips on the
 *  directory to render as "Default Pin" for every free-text tag in the
 *  DB ("smash burgers", "Hot dog", hashtag blobs, etc.) — see bug
 *  report from 2026-05-13. Now it returns null so callers can decide
 *  how to render unknowns. */
export function getCategory(slug) {
  return BY_SLUG[slug] || null;
}

/** Pin-style fallback variant. Use this only where you need a guaranteed
 *  non-null entry (map marker icon, schedule editor dropdown current
 *  value). Falls back to the "default" pin styling. */
export function getCategoryOrDefault(slug) {
  return BY_SLUG[slug] || BY_SLUG.default;
}

/** Best-effort human label for an arbitrary slug — uses the catalog
 *  label if available, otherwise title-cases the raw slug so messy
 *  free-text tags in the DB (`"smash burgers"`, `"BBQ Parfait"`) still
 *  render as the user typed them, not as "Default Pin". */
export function getCategoryLabel(slug) {
  if (!slug || typeof slug !== "string") return "";
  const cat = BY_SLUG[slug];
  if (cat) return cat.label;
  // Title-case for display while preserving acronyms (BBQ → BBQ, not Bbq).
  return slug
    .split(/\s+/)
    .map((word) => {
      if (!word) return word;
      // Words that are all-uppercase or contain digits stay as-is.
      if (word === word.toUpperCase()) return word;
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    })
    .join(" ");
}

/** Same whitelist as the backend — any string not in here falls back to
 *  "default" so old DB rows keep rendering cleanly. */
export const CATEGORY_SLUGS = FOOD_CATEGORIES.map((c) => c.slug);
